import { FC, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ArticleAny, ISummarySection } from "@/types"
import useAssetTechnicalDataFields from "hooks/useAssetTechnicalDataFields"
import useAssetSummaryTechnicalData from "hooks/useAssetSummaryTechnicalData"
import Summary from "components/Summary/Summary"
import { GlobalAssetOverviewAsset } from "models/GlobalAssetOverviewAsset"
import { IListItem } from "components/List/List"
import { getTranslation } from "utils/translations"
import { useTenantSettings } from "stores/tenantSettings"
import Lmid from "components/Lmid/Lmid"
import Features from "components/Features/Features"
import NoValue from "components/NoValue/NoValue"
import FreightUnitsItem from "components/FreightUnits/FreightUnitsItem"
import useAssetColor from "pages/AssetPage/hooks/useAssetColor"

interface IGlobalAssetOvewviewSidebarFieldsProps {
    asset: GlobalAssetOverviewAsset
    article: ArticleAny
}

const GlobalAssetOvewviewSidebarFields: FC<IGlobalAssetOvewviewSidebarFieldsProps> = ({
    asset,
    article,
}) => {
    const { t } = useTranslation()
    const { tenantSettings, tenantPrimaryLanguageIsoCode } = useTenantSettings()
    const currency = tenantSettings?.defaultCurrency

    const {
        color,
        initColorById,
    } = useAssetColor()

    useEffect(() => {
        if (asset?.colorId) {
            initColorById(asset.colorId)
        }
    }, [asset?.colorId, initColorById])


    const { getFields } = useAssetTechnicalDataFields({ asset, article, color })

    const technicalDataItems = useAssetSummaryTechnicalData({
        asset,
        article,
        getFields,
    })

    const summaryData: ISummarySection<IListItem>[] = useMemo(() => {
        if (!asset) return []

        const features = getTranslation(asset.features, tenantPrimaryLanguageIsoCode)

        const result: ISummarySection<IListItem>[] = [
            {
                title: t('asset.basicData'),
                items: [],
                subSections: [
                    {
                        title: t('asset.assetIdentification'),
                        items: [
                            {
                                label: t('common.lmid'),
                                value: <Lmid lmid={asset.lmid} labeled={asset.isLabeled} />,
                            },
                        ],
                    }
                ]
            },
            {
                title: t("asset.technicalData"),
                items: [
                    {
                        label: t('asset.yearOfConstruction'),
                        value: asset.constructionYear,
                    },
                    {
                        label: t('asset.operationalState'),
                        value: (asset.isOperational ? t('asset.assetOperational') : t('asset.assetOutOfOrder')),
                        valueTestId: "asset-operational-state"
                    },
                    ...technicalDataItems,
                ],
            },
            {
                title: t('asset.features'),
                items: [],
                subSections: [
                    {
                        title: t('asset.features'),
                        items: [{
                            label: features
                                ? <Features features={features} />
                                : <NoValue>{t('features.noFeaturesAdded')}</NoValue>
                        }]
                    },
                    {
                        title: t('freightUnit.freightUnits'),
                        items: asset.freightUnits && asset.freightUnits.length > 0 ? asset.freightUnits.map(fu => ({
                            label: <FreightUnitsItem freightUnit={fu} />
                        })) : [
                            { label: <NoValue>{t('freightUnit.noFreightUnitsAdded')}</NoValue> }
                        ]
                    }
                ],
            },
            {
                title: t("asset.finance"),
                items: [
                    {
                        label: t('asset.listPrice'),
                        value: typeof asset.listPrice === "number" ? t('common.amountWithCurrency', { count: asset.listPrice, currency }) : <NoValue />,
                    },
                    {
                        label: t('asset.listPriceDate'),
                        value: asset.listPriceDate ? asset.listPriceDate.format(t('config.dateLongFormat')) : <NoValue />,
                    },
                ],
            },
            {
                items: [
                    {
                        label: t('asset.assetCategory'),
                        value: asset.assetCategory || <NoValue />,
                    },
                    {
                        label: t('assetCategories.division'),
                        value: asset.division || <NoValue />,
                    },
                ],
            },
        ]

        return result
    }, [asset, currency, t, technicalDataItems, tenantPrimaryLanguageIsoCode])

    return <Summary data={summaryData} condensed />
}

export default GlobalAssetOvewviewSidebarFields
